import { useEffect, useMemo, useState } from 'react';
import { useDocumentTaxonomyConfig } from 'app/api/tag-queries/document-taxonomy-config';
import { usePresentationTree } from 'app/api/tag-queries/taxonomy';
import { useCalculationRules } from 'app/api/tag-queries/calculation-rules';
import { useExtensions, useDocumentExtensionSchema } from 'app/api/tag-queries';
import { useTagsByDocument } from 'app/api/tag-queries/tags';
import { mergeExtensionsIntoPresentationTree, pruneToUsedConcepts, formatTaxonomyName } from 'app/utils/tag-utils';
import { Typography } from 'app/components/ui';
import Spinner from 'app/components/Spinner';
import { i18n } from 'app/utils/i18n';
import { PresentationTreePane } from './panes/PresentationTreePane';
import { CalculationPane } from './panes/CalculationPane';
import { AnchoringPane } from './panes/AnchoringPane';
import { ValuesPane } from './ValuesPane';

import './TreeOverview.scss';

export const TreeOverview = ({ documentId }: { documentId: string }) => {
  const documentSuperId = documentId ? parseInt(documentId, 10) : undefined;

  const { data: config, isLoading: configLoading } = useDocumentTaxonomyConfig(documentSuperId);
  const selections = useMemo(() => config?.taxonomySelections ?? [], [config]);

  const [selectedTaxonomyId, setSelectedTaxonomyId] = useState<string | undefined>(undefined);
  const [selectedConceptName, setSelectedConceptName] = useState<string | null>(null);
  const [selectedNodeKey, setSelectedNodeKey] = useState<string | null>(null);

  const handleSelect = (conceptName: string, nodeKey?: string) => {
    setSelectedConceptName(conceptName);
    setSelectedNodeKey(nodeKey ?? null);
  };

  useEffect(() => {
    if (selectedTaxonomyId || selections.length === 0) return;
    const active = selections.find((s) => s.isActive);
    setSelectedTaxonomyId((active ?? selections[0]).taxonomyId);
  }, [selections, selectedTaxonomyId]);

  const { data: presentationTree, isLoading: treeLoading } = usePresentationTree(documentSuperId, selectedTaxonomyId);
  const { data: extensions } = useExtensions(documentSuperId, selectedTaxonomyId);
  const { data: extensionSchema } = useDocumentExtensionSchema(documentSuperId);
  const { data: calculationRules } = useCalculationRules(documentSuperId, selectedTaxonomyId);

  const { data: tagsData } = useTagsByDocument(documentSuperId, { include: ['concept', 'context', 'unit', 'metadata'] });
  const extensionPrefix = extensionSchema?.prefix ?? null;

  const usedConceptNames = useMemo(() => {
    const names = new Set<string>();
    for (const tag of tagsData?.tags ?? []) {
      const name = tag.concept?.name;
      if (!name) continue;
      names.add(name);
      if (extensionPrefix && name.startsWith(`${extensionPrefix}:`)) names.add(name.slice(extensionPrefix.length + 1));
    }
    return names;
  }, [tagsData, extensionPrefix]);

  const { taxonomyData: mergedTree, extensionConceptNames } = useMemo(
    () => mergeExtensionsIntoPresentationTree(presentationTree, extensions, extensionSchema?.prefix ?? null),
    [presentationTree, extensions, extensionSchema]
  );

  const presentationPreview = useMemo(() => pruneToUsedConcepts(mergedTree, usedConceptNames), [mergedTree, usedConceptNames]);
  const usedTagsList = useMemo(() => Array.from(usedConceptNames), [usedConceptNames]);

  if (configLoading) {
    return <Spinner />;
  }

  if (selections.length === 0) {
    return (
      <div className="tree-overview__config-prompt">
        <Typography variant="body-3">{i18n('tag.treeOverview.noTaxonomy')}</Typography>
      </div>
    );
  }

  const panes: Array<{ titleKey: string; content: React.ReactNode }> = [
    {
      titleKey: 'tag.treeOverview.pane.presentation',
      content: (
        <PresentationTreePane
          data={presentationPreview}
          extensionTags={extensionConceptNames}
          usedTags={usedTagsList}
          selectedConceptName={selectedConceptName}
          selectedNodeKey={selectedNodeKey}
          onSelect={handleSelect}
        />
      ),
    },
    {
      titleKey: 'tag.treeOverview.pane.calculation',
      content: <CalculationPane rules={calculationRules} selectedNodeKey={selectedNodeKey} onSelect={handleSelect} />,
    },
    {
      titleKey: 'tag.treeOverview.pane.anchoring',
      content: <AnchoringPane extensions={extensions} selectedNodeKey={selectedNodeKey} onSelect={handleSelect} />,
    },
  ];

  return (
    <div className="tree-overview">
      <div className="tree-overview__toolbar">
        <Typography variant="body-7" fontWeight="bold">
          {i18n('tag.treeOverview.taxonomyLabel')}
        </Typography>
        <select className="tree-overview__select" value={selectedTaxonomyId ?? ''} onChange={(e) => setSelectedTaxonomyId(e.target.value)}>
          {selections.map((s) => (
            <option key={s.taxonomyId} value={s.taxonomyId}>
              {formatTaxonomyName(s.taxonomy?.name ?? s.taxonomyId)}
            </option>
          ))}
        </select>
      </div>

      <div className="tree-overview__panes">
        {panes.map((pane) => (
          <section className="tree-overview__pane" key={pane.titleKey}>
            <header className="tree-overview__pane-header">{i18n(pane.titleKey)}</header>
            <div className="tree-overview__pane-body">{treeLoading ? <Spinner /> : pane.content}</div>
          </section>
        ))}
      </div>

      <div className="tree-overview__values">
        <header className="tree-overview__pane-header">{i18n('tag.treeOverview.pane.values')}</header>
        <div className="tree-overview__values-body">
          <ValuesPane documentSuperId={documentSuperId} selectedConceptName={selectedConceptName} extensionPrefix={extensionPrefix} />
        </div>
      </div>
    </div>
  );
};
